import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/lib/nav";

import { Button } from "@buildpy/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  page: number;
  pageCount: number;
  // 쪽 번호 → 주소. 검색어·칩 조건을 그대로 싣는 것은 부르는 화면 몫이다(`listHref`).
  hrefFor: (page: number) => string;
  className?: string;
}

// 보일 쪽 번호 — 처음·끝·현재 앞뒤 둘, 사이가 벌면 null(말줄임).
export const pageWindow = (page: number, pageCount: number): (number | null)[] => {
  const shown = new Set([1, pageCount]);
  for (let p = page - 2; p <= page + 2; p += 1) if (p >= 1 && p <= pageCount) shown.add(p);
  const sorted = [...shown].sort((a, b) => a - b);
  const out: (number | null)[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1]! > 1) out.push(null);
    out.push(p);
  });
  return out;
};

// 목록 아래 쪽 이동 — 링크라 새로고침·뒤로가기·공유에 살아남고, 한 쪽뿐이면 그리지 않는다.
const Pager = ({ page, pageCount, hrefFor, className }: Props) => {
  if (pageCount <= 1) return null;
  const prev = page > 1 ? Math.min(page - 1, pageCount) : null;
  const next = page < pageCount ? page + 1 : null;
  return (
    <nav aria-label="쪽 이동" className={cn("flex flex-wrap items-center justify-center gap-1", className)}>
      {prev ? (
        <Button asChild size="sm" variant="ghost">
          <Link aria-label="이전 쪽" href={hrefFor(prev)}>
            <ChevronLeft />
          </Link>
        </Button>
      ) : (
        <Button aria-label="이전 쪽" disabled size="sm" variant="ghost">
          <ChevronLeft />
        </Button>
      )}

      {pageWindow(Math.min(page, pageCount), pageCount).map((p, i) =>
        p === null ? (
          <span key={`gap-${i}`} aria-hidden className="px-1 text-sm text-muted-foreground">
            …
          </span>
        ) : (
          <Button key={p} asChild size="sm" variant={p === page ? "primary" : "ghost"}>
            <Link aria-current={p === page ? "page" : undefined} className="tabular-nums" href={hrefFor(p)}>
              {p}
            </Link>
          </Button>
        )
      )}

      {next ? (
        <Button asChild size="sm" variant="ghost">
          <Link aria-label="다음 쪽" href={hrefFor(next)}>
            <ChevronRight />
          </Link>
        </Button>
      ) : (
        <Button aria-label="다음 쪽" disabled size="sm" variant="ghost">
          <ChevronRight />
        </Button>
      )}
    </nav>
  );
};

export default Pager;
